import { ReactNode } from "react"
import { useSelector } from "react-redux"
import { CardText } from "reactstrap"

import { IModel } from "@api/schema"
import TranslatedHtml from "@components/common/TranslatedHtml"
import { withDynamicNamespaces } from "@components/hoc/withDynamicNamespaces"
import { AppState } from "@redux/reducer"
import { OnboardingType, selectOtherExistingOnboardingType } from "@redux/usecases/onboarding"
import { NamespaceShortcut, useDynamicTranslation } from "@services/i18n"

import OnboardingLoginRegisterInvitationButtons from "./OnboardingLoginRegisterInvitationButtons"


const usedNamespaces: NamespaceShortcut[] = ["onboarding"]

type OnboardingCreationSuccessMessageProps = {
  /**
   * Flag that defines if this is a successful creation as authenticated user (false)
   * or a temporary creation as a not-logged-in user (true).
   *
   * NOTE @todo: refactor to a more meaningful name, and calculate i18n keys automatically (saved/notSaved, thanks for creating/onboarding)
   */
  showWarningOfDataLoss: boolean
  thanksMessageI18nKey: string
  children: ReactNode
  infoMessageI18nKey: string
}

const OnboardingCreationSuccessMessageWithPlaceholder: React.FC<OnboardingCreationSuccessMessageProps> = (props: OnboardingCreationSuccessMessageProps) => {
  const t = useDynamicTranslation()

  return <>
    <CardText className="text-success">
      <TranslatedHtml content={t(null, props.thanksMessageI18nKey)} />
    </CardText>
    {props.children}
    <CardText className="text-info-container">
      <TranslatedHtml content={t(null, props.infoMessageI18nKey)} />
    </CardText>
    {props.showWarningOfDataLoss
      ? <CardText className="text-warning">
        <TranslatedHtml content={t("onboarding", "warningOfDataLoss")} />
      </CardText>
      : null
    }
  </>
}


export type IProps<T extends IModel> = {
  /**
   * Type of the onboarding process that is handled on the current page.
   */
  thisOnboardingType: OnboardingType

  /**
   * A method that returns the parameterized ReactNode that should be shown after successful creation
   * of the corresponding object.
   * The created object is either @see IProps.createdAuthenticatedObject or @see IProps.createdOnboardingObject.
   *
   * @param object The created object
   * @returns The ReactNode to dispay
   */
  creationSuccessUI: (object: T) => ReactNode

  /**
   * The object that has been created while the user has been logged in; it's stored in the DB.
   * Optional, b/c there may be usecases (like create-project) where the user is routed to another page.
   */
  createdAuthenticatedObject?: T
  authenticatedCreationThanksMessageI18nKey?: string
  authenticatedCreationInfoMessageI18nKey?: string
  /**
   * Additional UI elements that shall be shown after successful creation for logged-in users.
   */
  authenticatedCreationAdditionalUI?: ReactNode

  /**
   * If there is an onboarding object of the same type (@see IProps.thisOnboardingType) in the app state,
   * this flag decides whether we show its content (if the currently shown object is the origin of the onboarding object),
   * or we show a warning that there is another onboarding data of the same type (with another origin).
   */
  currentOriginIsOriginForOnboardingObject?: boolean
  /**
   * The i18n key used to display information when `currentOriginIsOriginForOnboardingObject` is true.
   */
  onboardingObjectIsFromAnotherOriginMessageI18nKey?: string

  /**
   * The object that has been created while the user was not logged in; it's stored in the onboarding state.
   */
  createdOnboardingObject: T
  onboardingCreationThanksMessageI18nKey: string
  onboardingCreationInfoMessageI18nKey: string
}

/**
 * This component helps to visualize the onboarding process (see doc/usecases/onboarding.md) for the user.
 *
 * When the unauthenticated user performs one of the interactions that are part of our onboarding usecase,
 * this component shall be utilized to show the according feedback, including buttons that route to login or register pages.
 */
const OnboardingComponent: React.FC<IProps<IModel>> = <T extends IModel>(props: IProps<T>) => {

  const t = useDynamicTranslation()
  const otherOnboardingType = useSelector((state: AppState) => selectOtherExistingOnboardingType(state, props.thisOnboardingType))

  return <>
    {props.createdAuthenticatedObject && <>
      <OnboardingCreationSuccessMessageWithPlaceholder
        showWarningOfDataLoss={false}
        thanksMessageI18nKey={props.authenticatedCreationThanksMessageI18nKey}
        // eslint-disable-next-line react/no-children-prop
        children={props.creationSuccessUI(props.createdAuthenticatedObject)}
        infoMessageI18nKey={props.authenticatedCreationInfoMessageI18nKey}
      />
      {props.authenticatedCreationAdditionalUI}
    </>}

    {props.createdOnboardingObject && <>
      {props.currentOriginIsOriginForOnboardingObject
        ? <OnboardingCreationSuccessMessageWithPlaceholder
          showWarningOfDataLoss={true}
          thanksMessageI18nKey={props.onboardingCreationThanksMessageI18nKey}
          // eslint-disable-next-line react/no-children-prop
          children={props.creationSuccessUI(props.createdOnboardingObject)}
          infoMessageI18nKey={props.onboardingCreationInfoMessageI18nKey}
        />
        : <CardText className="text-danger">
          <TranslatedHtml content={props.onboardingObjectIsFromAnotherOriginMessageI18nKey} />
        </CardText>
      }

      <OnboardingLoginRegisterInvitationButtons
        unsavedOnboardingType={props.thisOnboardingType}
      />
    </>}

    {otherOnboardingType && <>
      <CardText className="text-danger">
        <TranslatedHtml
          content={t("onboarding", "otherOnboardingDataExists",
            {
              existingOnboardingDataWithIndefiniteArticle: t("onboarding", otherOnboardingType + ".accusativeWithIndefiniteArticle"),
              requestedOnboardingDataWithIndefiniteArticle: t("onboarding", props.thisOnboardingType + ".accusativeWithIndefiniteArticle"),
            })}
        />
      </CardText>
      <OnboardingLoginRegisterInvitationButtons
        unsavedOnboardingType={otherOnboardingType}
      />
    </>
    }
  </>
}

export default withDynamicNamespaces<IProps<IModel>>(OnboardingComponent, usedNamespaces)
